import * as React from "react";
import { Toggle as TogglePrimitive } from "@base-ui/react/toggle";
import { ToggleGroup as ToggleGroupPrimitive } from "@base-ui/react/toggle-group";

import { cn } from "../../lib/utils";
import { toggleVariants, type ToggleVariantProps } from "./toggle-variants";

const ToggleGroupContext = React.createContext<
  ToggleVariantProps & {
    spacing?: number;
    orientation?: "horizontal" | "vertical";
  }
>({
  size: "default",
  variant: "outline",
  spacing: 0,
  orientation: "horizontal",
});

function ToggleGroup({
  className,
  variant,
  size,
  spacing = 0,
  orientation = "horizontal",
  children,
  ...props
}: ToggleGroupPrimitive.Props &
  ToggleVariantProps & {
    spacing?: number;
    orientation?: "horizontal" | "vertical";
  }) {
  const contextValue = React.useMemo(
    () => ({ variant, size, spacing, orientation }),
    [variant, size, spacing, orientation],
  );

  return (
    <ToggleGroupPrimitive
      data-slot="toggle-group"
      data-variant={variant}
      data-size={size}
      data-spacing={spacing}
      data-orientation={orientation}
      style={{ "--gap": spacing } as React.CSSProperties}
      className={cn(
        "flex items-center gap-[calc(var(--spacing-1)*var(--gap))] rounded-lg data-[variant=segmented]:gap-px data-[variant=segmented]:bg-tint-tertiary data-[size=sm]:rounded-[min(var(--radii-md),10px)] data-[orientation=vertical]:flex-col data-[orientation=vertical]:items-stretch",
        className,
      )}
      {...props}
    >
      <ToggleGroupContext.Provider value={contextValue}>
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive>
  );
}

function ToggleGroupItem({
  className,
  children,
  variant = "outline",
  size = "default",
  ...props
}: TogglePrimitive.Props & ToggleVariantProps) {
  const context = React.use(ToggleGroupContext);
  const resolvedVariant = context.variant || variant;
  const resolvedSize = context.size || size;

  return (
    <TogglePrimitive
      data-slot="toggle-group-item"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-spacing={context.spacing}
      className={cn(
        "shrink-0 focus-visible:z-[1] data-[focus-visible]:z-[1] data-[focus]:z-[1] [data-slot=toggle-group][data-variant=segmented]_&:flex-1 [data-slot=toggle-group][data-variant=segmented]_&:rounded-md [data-slot=toggle-group][data-variant=segmented]_&:px-3 [data-slot=toggle-group][data-variant=segmented]_&:py-1 [data-slot=toggle-group][data-spacing='0']_&:rounded-none [data-slot=toggle-group][data-spacing='0']_&:px-2 [data-slot=toggle-group][data-spacing='0']_&:has([data-icon=inline-end]):pr-1.5 [data-slot=toggle-group][data-spacing='0']_&:has([data-icon=inline-start]):pl-1.5 [data-slot=toggle-group][data-orientation=horizontal][data-spacing='0']_&:-ml-px [data-slot=toggle-group][data-orientation=horizontal][data-spacing='0']_&:first-child:ml-0 [data-slot=toggle-group][data-orientation=horizontal][data-spacing='0']_&:first-child:rounded-l-lg [data-slot=toggle-group][data-orientation=horizontal][data-spacing='0']_&:last-child:rounded-r-lg [data-slot=toggle-group][data-orientation=vertical][data-spacing='0']_&:-mt-px [data-slot=toggle-group][data-orientation=vertical][data-spacing='0']_&:first-child:mt-0 [data-slot=toggle-group][data-orientation=vertical][data-spacing='0']_&:first-child:rounded-t-lg [data-slot=toggle-group][data-orientation=vertical][data-spacing='0']_&:last-child:rounded-b-lg",
        toggleVariants({
          variant: resolvedVariant,
          size: resolvedSize,
        }),
        className,
      )}
      {...props}
    >
      {children}
    </TogglePrimitive>
  );
}

export { ToggleGroup, ToggleGroupItem };
